<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import subDays from 'date-fns/subDays';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import getUnixTime from 'date-fns/getUnixTime';
import fromUnixTime from 'date-fns/fromUnixTime';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import ReportHeader from './components/ReportHeader.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import { useAlert } from 'dashboard/composables';

const MAX_PERIOD_DAYS = 90;

const store = useStore();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const whatsappInboxes = computed(() =>
  store.getters['inboxes/getWhatsAppInboxes'].map(inbox => ({
    ...inbox,
    label: inbox.phone_number
      ? `${inbox.name} (${inbox.phone_number})`
      : inbox.name,
  }))
);

const selectedInbox = ref(null);
const dateRange = ref([subDays(new Date(), 29), new Date()]);

const inboxId = computed(() => selectedInbox.value?.id);
const since = computed(() => getUnixTime(startOfDay(dateRange.value[0])));
const until = computed(() => getUnixTime(endOfDay(dateRange.value[1])));
const hasInbox = computed(() => !!selectedInbox.value);

// El detalle (meta_reports_template) también cuenta como pestaña "Plantillas".
const templateRoutes = ['meta_reports_templates', 'meta_reports_template'];
const activeTab = computed(() => (templateRoutes.includes(route.name) ? 1 : 0));
const tabs = computed(() => [
  { label: t('META_REPORTS.TABS.MESSAGES'), key: 'messages' },
  { label: t('META_REPORTS.TABS.TEMPLATES'), key: 'templates' },
]);

const onTabChange = tab => {
  const name = tab.key === 'templates' ? 'meta_reports_templates' : 'meta_reports';
  if (route.name !== name) router.push({ name, query: route.query });
};

// Meta limita template_analytics a 90 días; validamos la selección y la
// recortamos a los últimos 90 días del rango elegido si se pasa.
const onDateRangeChange = value => {
  const [start, end] = value;
  if (differenceInCalendarDays(end, start) + 1 > MAX_PERIOD_DAYS) {
    useAlert(t('META_REPORTS.MAX_PERIOD'));
    dateRange.value = [subDays(end, MAX_PERIOD_DAYS - 1), end];
    return;
  }
  dateRange.value = value;
};

// Espejamos inbox/rango en el query para que el link del detalle sea
// recargable y compartible (el :templateId ya viaja en el path).
watch([inboxId, since, until], () => {
  if (!inboxId.value) return;
  router
    .replace({
      query: {
        ...route.query,
        inbox_id: inboxId.value,
        since: since.value,
        until: until.value,
      },
    })
    .catch(() => {});
});

onMounted(() => {
  const query = route.query;
  if (query.since && query.until) {
    dateRange.value = [
      fromUnixTime(Number(query.since)),
      fromUnixTime(Number(query.until)),
    ];
  }
  const inboxes = whatsappInboxes.value;
  if (!inboxes.length) return;
  const fromQuery =
    query.inbox_id &&
    inboxes.find(inbox => String(inbox.id) === String(query.inbox_id));
  selectedInbox.value = fromQuery || inboxes[0];
});
</script>

<template>
  <div class="flex flex-col h-full">
    <ReportHeader
      :header-title="$t('META_REPORTS.HEADER')"
      :header-description="$t('META_REPORTS.DESCRIPTION')"
    />

    <div class="px-8 pt-4 flex items-center gap-4 flex-wrap">
      <div class="multiselect-wrap--small w-64">
        <multiselect
          v-model="selectedInbox"
          track-by="id"
          label="label"
          :placeholder="$t('META_REPORTS.SELECT_INBOX')"
          :options="whatsappInboxes"
          :searchable="false"
          :allow-empty="false"
          :show-labels="false"
        />
      </div>
      <WootDateRangePicker
        :value="dateRange"
        :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
        :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
        @change="onDateRangeChange"
      />
    </div>

    <div class="px-8 pt-4">
      <TabBar
        :tabs="tabs"
        :initial-active-tab="activeTab"
        @tab-changed="onTabChange"
      />
    </div>

    <div class="flex-1 px-8 py-6 overflow-auto">
      <div v-if="!hasInbox" class="text-n-slate-11 py-10 text-center">
        {{ $t('META_REPORTS.NO_INBOX') }}
      </div>
      <router-view
        v-else
        :inbox-id="inboxId"
        :since="since"
        :until="until"
      />
    </div>
  </div>
</template>
